import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { BatchRule, BatchRuleType } from "../../domain/batch-rule";
import { UI } from "../../services/ui.service";
import {
  BatchRulesRepository,
  CheckResultEntry,
  EMediaExistingHandling,
} from "../../domain/batch-rules-repository";
import { Subscription, from as observableFrom } from "rxjs";
import { Logger } from "../../infrastructure/logger";
import { UploadService } from "../../infrastructure/upload/upload-service";
import { UserService } from "../../domain/user-service";
import {
  S3Bucket,
  TenantStorageOption,
  ITenantStorageOptionRepository,
} from "../../domain/tenant-storage-option";
import { IUploadFile } from "../../domain/Upload/i-upload-file";
import { User } from "../../domain/user";
import { ICommonUtilInterface } from "../../domain/common/common-util.interface";
import { BatchRuleViewModel } from "./batch-rule.view-model";

@Component({
  selector: "icn-batch-import",
  templateUrl: "./batch-import.html",
  styleUrls: ["./batch-import.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchImportComponent implements OnInit, OnDestroy {
  editedRule: BatchRuleViewModel;

  rules: BatchRuleViewModel[] = [];
  get isAllChecked() {
    return !this.rules.find((x) => !x.selected);
  }
  showGoButton = false;
  showExistingMediaHandlingButtons = false;
  ruleAddedSubscription: Subscription;
  ruleUpdatedSubscription: Subscription;
  batchFileReadyForUploadSubscription: Subscription;
  uploadPath = "";

  summary: CheckResultEntry[];

  fileUploadSubs: Subscription[] = [];
  storageOptions: S3Bucket[];
  selectedStorageOption: S3Bucket;
  currentUser: User;

  constructor(
    private batchRulesRepository: BatchRulesRepository,
    private changeDetectorRef: ChangeDetectorRef,
    private ui: UI,
    private logger: Logger,
    public uploadService: UploadService,
    private userService: UserService,
    private _tenantStorageOptionRepository: ITenantStorageOptionRepository,
    private _commonUtils: ICommonUtilInterface,
  ) {}

  formatProgress(file: IUploadFile) {
    if (file.isError) {
      return "Error";
    } else if (file.isComplete) {
      return "Done";
    } else {
      return (file.progress * 100).toFixed(0) + "%";
    }
  }

  ngOnInit() {
    this.currentUser = this.userService.getCurrentUser();
    this.storageOptions = this.currentUser.tenantStorageOptions as S3Bucket[];
    if (this.storageOptions.length > 0) {
      this.selectedStorageOption = this.storageOptions[0];
    }

    this.batchRulesRepository.getRules().then((rules) => {
      rules.forEach((x) => {
        this.rules.push(new BatchRuleViewModel(x, false));
      });
      this.changeDetectorRef.markForCheck();
    });

    this.ruleAddedSubscription = this.batchRulesRepository.ruleAdded.subscribe(
      (rule) => {
        this.ruleAdded(rule);
      },
    );

    this.ruleUpdatedSubscription =
      this.batchRulesRepository.ruleUpdated.subscribe((rule) => {
        this.ruleUpdated(rule);
      });
    this.uploadService.batchUploadFiles.forEach((file) => {
      this.fileUploadSubs.push(
        file.progressChange.subscribe((res) => {
          this.changeDetectorRef.markForCheck();
        }),
      );
    });
    this.uploadService.batchUploadFiles.forEach((file) => {
      this.fileUploadSubs.push(
        file.isErrorChange.subscribe((res) => {
          this.changeDetectorRef.markForCheck();
        }),
      );
    });
    this.uploadService.batchUploadFiles.forEach((file) => {
      this.fileUploadSubs.push(
        file.isCompleteChange.subscribe((res) => {
          this.changeDetectorRef.markForCheck();
        }),
      );
    });
    this.batchFileReadyForUploadSubscription = observableFrom(
      this.uploadService.batchFileReadyForUpload,
    ).subscribe(() => {
      if (this.uploadService.batchUploadFiles.length > 0) {
        const lastItemIndex = this.uploadService.batchUploadFiles.length - 1;
        this.fileUploadSubs.push(
          this.uploadService.batchUploadFiles[
            lastItemIndex
          ].progressChange.subscribe(() => {
            this.changeDetectorRef.markForCheck();
          }),
        );
        this.fileUploadSubs.push(
          this.uploadService.batchUploadFiles[
            lastItemIndex
          ].isErrorChange.subscribe(() => {
            this.changeDetectorRef.markForCheck();
          }),
        );
        this.fileUploadSubs.push(
          this.uploadService.batchUploadFiles[
            lastItemIndex
          ].progressChange.subscribe(() => {
            this.changeDetectorRef.markForCheck();
          }),
        );
      }
    });
  }

  ruleAdded(rule: BatchRule) {
    this.rules.push(new BatchRuleViewModel(rule, false));
    this.changeDetectorRef.markForCheck();
  }

  ruleUpdated(rule: BatchRule) {
    const index = this.rules.findIndex((x) => x.id === rule.id);
    this.rules[index] = new BatchRuleViewModel(
      rule,
      this.rules[index].selected,
    );
  }

  toggleCheckAll() {
    if (this.isAllChecked) {
      this.uncheckAll();
    } else {
      this.checkAll();
    }
  }

  checkAll() {
    for (let i = 0; i < this.rules.length; i++) {
      this.rules[i].selected = true;
    }
  }

  uncheckAll() {
    for (let i = 0; i < this.rules.length; i++) {
      this.rules[i].selected = false;
    }
  }

  /*toggleCheckRule(rule: BatchRuleViewModel) {
        rule.selected = !rule.selected;
    }*/

  openAddRulePopup(event, popover) {
    const editedRule = new BatchRule(
      undefined,
      undefined,
      "",
      "",
      BatchRuleType.Video,
      undefined,
      "en",
      "en",
      25,
      0,
    );
    this.editedRule = new BatchRuleViewModel(editedRule, false);
    popover.show(event);
  }

  openEditRulePopup(event, rule: BatchRuleViewModel, rulePopover) {
    /*const editedRule =
            new BatchRule(rule.id, rule.name, rule.format, rule.idFormat, rule.type, rule.dateCreated, rule.audioLanguage, rule.subtitlesLanguage, rule.subtitlesFps, rule.subtitlesOffset);*/
    this.editedRule = rule.clone();
    rulePopover.show(event);
  }

  formatType(rule: BatchRule) {
    return BatchRuleType[rule.type];
  }

  formatDate(rule: BatchRule) {
    return rule.dateCreated.format("DD.MM.YYYY");
  }

  getCheckedRules() {
    return this.rules.filter((r) => r.selected);
  }

  onClickDelete(rule: BatchRuleViewModel) {
    this.ui.notify(this.ui.EVENTS.ShowConfirm, {
      title: "Delete batch rules",
      message: `Are you sure that you want to delete ${this.getCheckedRules().length || 1} rules?`,
      buttons: [
        {
          title: "Yes",
          callback: () => {
            this.onDeleteConfirmed(rule);
          },
        },
        {
          title: "No",
          callback: () => {},
        },
      ],
    });
  }

  onDeleteConfirmed(rule: BatchRuleViewModel) {
    let rules = this.getCheckedRules();
    if (rules.length === 0) {
      rules = [rule];
    }
    const indices = [];
    rules.forEach((x) => {
      indices.push(this.rules.indexOf(x));
    });
    indices.sort((a, b) => {
      return b - a;
    });

    this.batchRulesRepository
      .deleteRules(rules)
      .then(() => {
        indices.forEach((x) => {
          this.rules.splice(x, 1);
        });
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        return this.logger.error(err);
      });
  }

  checkRules() {
    if (!this.selectedStorageOption) {
      return;
    }
    this.summary = [];
    const selectedRuleIds = this.getCheckedRules().map((r) => r.id);
    this.batchRulesRepository
      .check(this.uploadPath, this.selectedStorageOption, selectedRuleIds)
      .then((result) => {
        /*const summary = [];
            if (result.unrecognized.length > 0) {
                summary.push({title: `${result.unrecognized.length} unrecognized`, fileNames: result.unrecognized});
            }

            for (let ruleId in result.filesByRuleIds) {

                let rule = this.rules.find(x => x.id === parseInt(ruleId));
                let notAssociatedFiles = 0;
                result.filesByRuleIds[ruleId].forEach(x => {
                    if (!x.isAssociated) {
                        notAssociatedFiles++;
                    }
                });

                let notAssociatedFilesText = notAssociatedFiles ? ` (${notAssociatedFiles} without movies)` : '';

                summary.push({
                    title:
                    `${rule ? rule.name : 'Unknown rule'}: ${result.filesByRuleIds[ruleId].length}` + notAssociatedFilesText,
                    fileNames: result.filesByRuleIds[ruleId].map(x => x.name)
                });
            }*/
        this.summary = result.sort((a, b) =>
          a.ruleName > b.ruleName ? 1 : -1,
        );
        if (this.summary.find((x) => x.existingMediaId)) {
          this.showGoButton = false;
          this.showExistingMediaHandlingButtons = true;
        } else {
          this.showGoButton = true;
          this.showExistingMediaHandlingButtons = false;
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  go(mediaExistingHandling: EMediaExistingHandling) {
    if (!this.selectedStorageOption) {
      return;
    }

    const deadlineOffsetHours = this._promptForDeadlineHours();
    if (deadlineOffsetHours !== null) {
      const selectedRuleIds = this.getCheckedRules().map((r) => r.id);
      this.batchRulesRepository
        .go(
          this.uploadPath,
          this.selectedStorageOption,
          deadlineOffsetHours,
          mediaExistingHandling,
          selectedRuleIds,
        )
        .then(() => {
          this.ui.notify(this.ui.EVENTS.ShowAlert, {
            title: "Batch Import",
            message: "Done!",
          });
          this.showGoButton = false;
        })
        .catch((err) => {
          this.logger.error("Could not set rules: " + err);

          this.showGoButton = false;
        });
    }
  }

  ngOnDestroy() {
    if (this.ruleAddedSubscription) {
      this.ruleAddedSubscription.unsubscribe();
    }
    if (this.ruleUpdatedSubscription) {
      this.ruleUpdatedSubscription.unsubscribe();
    }
    this.fileUploadSubs.forEach((sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });
    if (this.batchFileReadyForUploadSubscription) {
      this.batchFileReadyForUploadSubscription.unsubscribe();
    }
  }

  onInputFileChange(e) {
    if (!this.selectedStorageOption) {
      e.target.value = "";
      return;
    }
    if (e.target.files.length === 0) {
      return;
    } else {
      for (let i = 0; i < e.target.files.length; i++) {
        this.uploadService.uploadFileToTenantBucket(
          this.uploadPath,
          e.target.files[i],
          this.selectedStorageOption,
        );
      }
      e.target.value = "";
    }
  }

  clear() {
    for (let i = 0; i < this.uploadService.batchUploadFiles.length; i++) {
      if (this.uploadService.batchUploadFiles[i].isComplete) {
        this.uploadService.batchUploadFiles.splice(i, 1);
      }
    }
  }

  private _promptForDeadlineHours(): number {
    const response = prompt("Please specify the deadline offset hours");

    if (response === null) {
      return null;
    }
    // tslint:disable-next-line:radix
    if (isNaN(parseInt(response)) === false) {
      // tslint:disable-next-line:radix
      const deadLineOffsetHours = parseInt(response);
      return deadLineOffsetHours;
    } else {
      alert(
        "Failed to parse the deadline offset hours. It must be a number value. Please try again...",
      );
      return this._promptForDeadlineHours();
    }
  }

  async openNewTenantOptionWizard() {
    const result = window.prompt(
      'Please write bucket details separated by ";". For example: DisplayName;BucketName;Region;AccessKey;SecretKey;ServiceUrl',
    );
    if (result) {
      const options = result.split(";");
      console.log(options);
      if (options.length !== 6) {
        alert("Please check the details and try again");
      } else {
        const uid = this._commonUtils.generateGuid();
        const displayName = options[0];
        const bucketName = options[1];
        const region = options[2];
        const accesKey = options[3];
        const secretKey = options[4];
        const serviceUrl = options[5];

        if (
          window.confirm(
            `Please verify the details:\n\n DisplayName: ${displayName}\n BucketName: ${bucketName}\n Region: ${region}\n AccessKey: ${accesKey}\n SecretKey: ${secretKey}\n ServiceUrl: ${serviceUrl}`,
          )
        ) {
          try {
            await this._tenantStorageOptionRepository.insertNew(
              uid,
              displayName,
              bucketName,
              region,
              accesKey,
              secretKey,
              serviceUrl,
            );
            const newBucketOptions = new S3Bucket(
              uid,
              displayName,
              bucketName,
              region,
              accesKey,
            );

            this.storageOptions.push(newBucketOptions);
            if (this.storageOptions.length === 1) {
              this.selectedStorageOption = null;
              this.changeDetectorRef.detectChanges();
              this.changeDetectorRef.markForCheck();
              this.selectedStorageOption = this.storageOptions[0];
            }

            alert("New Tenant Storage Option Has Been Added Successfully!");

            this.changeDetectorRef.detectChanges();
            this.changeDetectorRef.markForCheck();
          } catch (e) {
            console.log("batch-import->ERROR", e);
            alert("Error!\n" + e);
          }
        }
      }
    }
  }

  async deleteSelectedOption() {
    if (
      window.confirm(
        `Are you sure you want to delete bucket option: "${this.selectedStorageOption.displayName}"?`,
      )
    ) {
      try {
        await this._tenantStorageOptionRepository.delete(
          this.selectedStorageOption,
        );

        this.storageOptions = this.storageOptions.filter(
          (x) => x.id !== this.selectedStorageOption.id,
        );
        if (this.storageOptions.length > 0) {
          this.selectedStorageOption = this.storageOptions[0];
        } else {
          this.selectedStorageOption = null;
        }
        this.changeDetectorRef.markForCheck();
        alert("Bucked Option has been deleted successfully!");
      } catch (e) {
        console.log("batch-import->ERROR", e);
        alert("Error!\n" + e);
      }
    }
  }

  defineResultRowColor(summaryItem: CheckResultEntry) {
    if (summaryItem.existingMediaId) {
      return "lightcoral";
    } else if (summaryItem.status === "Recognized") {
      return "aquamarine";
    } else if (
      summaryItem.status === "Recognized without movie" ||
      summaryItem.status === "Unrecognized ID FORMAT"
    ) {
      return "bisque";
    }
  }

  summarizeCheckResult(summary: CheckResultEntry[]) {
    let result = "";
    if (summary && summary.length > 0) {
      const unrecognizedCount = summary.filter((x) =>
        x.status.includes("Unrecognized"),
      ).length;
      const recognizedWithoutMoviesCount = summary.filter((x) =>
        x.status.includes("Recognized without movie"),
      ).length;
      const recognizedByRule = {};

      for (let i = 0; i < summary.length; i++) {
        const summaryEntry = summary[i];
        if (summaryEntry.ruleName.length > 0) {
          if (!recognizedByRule[summaryEntry.ruleName]) {
            recognizedByRule[summaryEntry.ruleName] = 1;
          } else {
            recognizedByRule[summaryEntry.ruleName] += 1;
          }
        }
      }
      console.log(
        "batch-import->summarizeCheckResult",
        unrecognizedCount,
        recognizedByRule,
      );
      if (unrecognizedCount > 0) {
        result += `Unrecognized: ${unrecognizedCount}</br>`;
      }
      if (recognizedWithoutMoviesCount > 0) {
        result += `Recognized without movie: ${recognizedWithoutMoviesCount}</br>`;
      }
      // tslint:disable-next-line:forin
      for (const ruleName in recognizedByRule) {
        result += `<b>${ruleName}</b>: ${recognizedByRule[ruleName]}</br>`;
      }
    }
    return result;
  }
}
